import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Disclaimer from 'components/disclaimer-section';
import ResultsGrid from 'features/results-grid';
import ResultsRestartButton from 'features/results-restart-button';
import TopActions from 'features/top-actions';
import { HS_FORM_PORTAL, HS_FORM_REGION, STEEL_ROUTES } from 'shared/constants';
import useIsMobile from 'shared/hooks/useIsMobile';
import useRestartCalculator from 'shared/hooks/useRestartCalculator';
import useSessionStorage from 'shared/hooks/useSessionStorage';
import { SESSION_STORAGE_KEYS } from 'shared/sessionStorageUtils';
import {
  CalculationInputParamsData,
  SteelCalculationInputParamsData,
} from 'shared/types';
import useGetSteelCalculationsList from '../api/useGetSteelCalculationsList';
import { MobileWrapper, ResultsLayout, Title } from './styles';
import ModalComponent from '../../../components/modal';
import ModalHeader from '../../../components/modal/modal-header';
import HubspotForm from '../../../components/modal/modal-form';
import SteelResultFilters from '../../../components/result-filters/steel-filters';

const SteelResults = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [isRecalculating, setIsRecalculating] = useState(false);
  const restartCalculator = useRestartCalculator();

  const [, setInputParamsFromResults] = useSessionStorage(
    SESSION_STORAGE_KEYS.inputParametersFromResults
  );

  const {
    productsWithCalculations,
    getSteelCalculationsList,
    isLoading: isCalculationsListLoading,
  } = useGetSteelCalculationsList();


  const navigateBack = () => navigate(STEEL_ROUTES.fireRating);

  const viewProductDetails = (inputParams: CalculationInputParamsData) => {
    const steelInputParams = inputParams as SteelCalculationInputParamsData;
    setInputParamsFromResults({
      productId: steelInputParams.productId,
      category: steelInputParams.category,
      fireRatingId: steelInputParams.fireRatingId,
      orientationId: steelInputParams.orientationId,
      totalAreaToProtectM2: steelInputParams.totalAreaToProtectM2,
      criticalTemperatureId: steelInputParams.criticalTemperatureId,
      criticalTemperatureC: steelInputParams.criticalTemperatureC,
      crossSectionalShapeId: steelInputParams.crossSectionalShapeId,
      sectionFactor: steelInputParams.sectionFactor,
      profileSubTypeId: steelInputParams.profileSubTypeId,
      protectedSideId: steelInputParams.protectedSideId,
    });
    navigate(`${STEEL_ROUTES.productDetails}/${steelInputParams.productId}`);
  };

  useEffect(() => {
    getSteelCalculationsList();
  }, []);

  useEffect(() => {
    if(isRecalculating) {
      getSteelCalculationsList();
      setIsRecalculating(false);
    }
  }, [isRecalculating]);

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [modalData, setModalData] = useState({
    title: '',
    portalId: '',
    formId: '',
    region: '',
  });

  const onContactSupportModalOpen = () => {
    setModalData({
      title: t('modal.contactSupport'),
      portalId: HS_FORM_PORTAL,
      formId: t('hubspot.contactSupportFormId'),
      region: HS_FORM_REGION,
    });

    setIsModalOpen(true);
  };

  return (
    <div>
      <ModalComponent
        header={
          <ModalHeader
            title={modalData.title}
            handleClose={() => {
              setIsModalOpen(false);
            }}
          />
        }
        isOpen={isModalOpen}
        content={
          <HubspotForm
            portalId={modalData.portalId}
            formId={modalData.formId}
            region={modalData.region}
            onFormSubmitted={() =>
              setModalData((prevState) => ({ ...prevState, title: '' }))
            }
            onDoneClick={() => {
              setIsModalOpen(false);
            }}
          />
        }
      />
      <TopActions
        title={t('results.title') ?? ''}
        onBackClick={navigateBack}
        rightAction={<ResultsRestartButton onClick={restartCalculator} />}
      />
      {isMobile ? (
        <MobileWrapper>
          <SteelResultFilters setIsRecalculating={setIsRecalculating}/>
          <ResultsGrid
            isLoadingData={isCalculationsListLoading}
            products={productsWithCalculations}
            onViewProductDetails={viewProductDetails}
            onContactSupportModalOpen={onContactSupportModalOpen}
          />
          { !isCalculationsListLoading &&
            <Disclaimer text={t('results.legal')} />
          }
        </MobileWrapper>
      ) : (
        <>
          <Title title={t('results.title')} />
          <ResultsLayout>
            <SteelResultFilters setIsRecalculating={setIsRecalculating}/>
            <ResultsGrid
              isLoadingData={isCalculationsListLoading}
              products={productsWithCalculations}
              onViewProductDetails={viewProductDetails}
              onContactSupportModalOpen={onContactSupportModalOpen}
            />
          </ResultsLayout>
          { !isCalculationsListLoading &&
            <Disclaimer text={t('results.legal')} />
          }
        </>
      )}
    </div>
  );
};

export default SteelResults;
